<template>
    <div
        id="order-modal"
        tabindex="-1"
        aria-hidden="true"
        class="fixed top-0 left-0 right-0 z-50 hidden w-full py-16 overflow-x-hidden overflow-y-auto md:inset-0 h-modal md:h-full"
    >
        <div v-if="_order" class="relative w-full h-full max-w-md md:h-auto">
            <!-- Modal content -->
            <div class="relative bg-white shadow">
                <button
                    type="button"
                    class="absolute top-3 right-2.5 text-gray bg-transparent hover:bg-gray-light hover:text-gray-dark text-sm p-1.5 ml-auto inline-flex items-center"
                    @click="closeModal"
                >
                    <svg aria-hidden="true" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    <span class="sr-only">Close modal</span>
                </button>
                <div class="px-6 py-6 lg:px-8">
                    <div class="mb-4">
                        Bestellung
                        <template v-if="picked_date">für {{ dayjs(picked_date).locale("de").format("dd, DD.MM.YYYY") }}</template>
                    </div>

                    <div v-show="!_order.options || !_order.options.fixeddate" class="mb-4">
                        <label for="date-picker" class="block uppercase tracking-wide text-xs font-bold mb-2">Datum</label>
                        <Skeletor v-if="!dates_with_menu" />
                        <vue-date-picker
                            v-else
                            id="date-picker"
                            v-model="picked_date"
                            :enable-time-picker="false"
                            :min-date="dayjs().format()"
                            :allowed-dates="dates_with_menu"
                            :auto-apply="true"
                            :prevent-min-max-navigation="true"
                            :six-weeks="true"
                            :clearable="false"
                            locale="de-DE"
                            :format-locale="de"
                            cancelText="Abbrechen"
                            selectText="Auswählen"
                            format="EEEEEE., dd.MM.yyyy"
                            class="apperance-none block w-full bg-white text-black border border-gray py-1 leading-tight focus:outline-none focus:bg-white focus:border-purple focus:ring-0 transition-colors duration-300 placeholder-gray"
                        />
                        <div v-if="errors.date" class="text-red-500 text-sm text-right px-1">{{ errors.date[0] }}</div>
                    </div>

                    <div class="mb-4">
                        <select-input
                            v-show="!_order.options || !_order.options.fixedcustomer"
                            label="Kunde"
                            v-model:input="_order.customer.id"
                            :error="errors.customer_id ? errors.customer_id[0] : ''"
                            @change="
                                () => {
                                    _order.customer = customers.find((customer) => customer.id === _order.customer.id);
                                    errors.customer_id = '';
                                }
                            "
                        >
                            <option v-for="(customer, index) in customers" :key="index" :value="customer.id">
                                {{ customer.name }}, {{ customer.address }} ({{ customer.tour.name }})
                            </option>
                        </select-input>
                        <Skeletor v-if="!dates_with_menu || !weekmenu.meals" height="284" />
                        <div v-else-if="!weekmenu || !weekmenu.meals || weekmenu.meals.length === 0" class="text-red-500 text-center px-1">
                            Für dieses Datum sind keine Gerichte hinterlegt.
                        </div>
                        <template v-else>
                            <div v-for="(ordermeal, index) in _order.ordermeals" :key="index">
                                <div class="flex gap-2 items-center">
                                    <div class="flex grow">
                                        <select-input
                                            v-if="weekmenu"
                                            class="grow"
                                            label="Gericht"
                                            v-model:input="ordermeal.meal_id"
                                            :error="errors['ordermeals.' + index + '.meal.id'] ? errors['ordermeals.' + index + '.meal.id'][0] : ''"
                                            @change="
                                                () => {
                                                    ordermeal.meal = weekmenu.meals.find((meal) => meal.id === ordermeal.meal_id);
                                                    errors['ordermeals.' + index + '.meal.id'] = '';
                                                }
                                            "
                                        >
                                            <option v-for="(weekmeal, index) in weekmenu.meals" :key="index" :value="weekmeal.id">
                                                {{ weekmeal.name }}
                                                <template v-if="weekmeal.is_veggie">(veg)</template>
                                            </option>
                                        </select-input>
                                        <text-input
                                            class="basis-32"
                                            label="Menge"
                                            inputType="number"
                                            :min="1"
                                            v-model:input="ordermeal.quantity"
                                            :error="errors['ordermeals.' + index + '.quantity'] ? errors['ordermeals.' + index + '.quantity'][0] : ''"
                                        />
                                    </div>
                                    <font-awesome-icon
                                        :class="ordermeal.disabled ? 'text-gray-light' : 'text-red-700'"
                                        class="cursor-pointer"
                                        icon="fa-solid fa-trash-alt"
                                        @click="deleteOrderMeal(ordermeal)"
                                    />
                                </div>
                            </div>
                            <button
                                class="font-montserrat text-xs block w-full bg-purple hover:bg-black text-white rounded-sm py-3 mb-2 transition-colors duration-300 uppercase tracking-widest font-bold"
                                @click="addMeal"
                            >
                                <font-awesome-icon icon="fa-solid fa-plus" />
                                &nbsp;Gericht hinzufügen
                            </button>
                            <div v-if="errors.ordermeals" class="text-red-500 text-sm text-right px-1">{{ errors.ordermeals[0] }}</div>
                            <textarea-input label="Hinweise" placeholder="Hinweise" v-model:input="_order.notes" :error="errors.notes ? errors.notes[0] : ''" />
                            <checkbox-input label="Bezahlt" v-model:input="_order.is_payed" v-if="_order.ordermeals.find((o) => o.meal.id !== null)" />
                        </template>
                    </div>
                    <button
                        v-if="!_order.options || !_order.options.fixeddate"
                        class="font-montserrat text-xs block w-full bg-purple hover:bg-black text-white rounded-sm py-3 mb-4 transition-colors duration-300 uppercase tracking-widest font-bold disabled:bg-gray-lighter disabled:text-gray-light"
                        type="submit"
                        @click="saveOrder(false)"
                        :disabled="_order.ordermeals.find((o) => o.meal.id === null)"
                    >
                        <font-awesome-icon icon="fa-solid fa-file-circle-plus" />
                        &nbsp;Speichern und weiter
                    </button>
                    <button
                        class="font-montserrat text-xs block w-full bg-purple-dark hover:bg-black text-white rounded-sm py-3 mb-4 transition-colors duration-300 uppercase tracking-widest font-bold disabled:bg-gray-lighter disabled:text-gray-light"
                        type="submit"
                        @click="saveOrder"
                        :disabled="_order.ordermeals.find((o) => o.meal.id === null)"
                    >
                        <font-awesome-icon icon="fa-solid fa-file-circle-plus" />
                        &nbsp;Speichern und schließen
                    </button>
                    <button
                        class="font-montserrat text-xs block w-full bg-red-600 hover:bg-black text-white rounded-sm py-3 transition-colors duration-300 uppercase tracking-widest font-bold disabled:bg-gray-lighter disabled:text-gray-light"
                        type="submit"
                        @click="deleteOrder"
                        :disabled="!_order.id"
                    >
                        <font-awesome-icon icon="fa-solid fa-trash-alt" />
                        &nbsp;Bestellung löschen
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, inject, onMounted, ref, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import SelectInput from "@/components/global/SelectInput.vue";
import CheckboxInput from "@/components/global/CheckboxInput.vue";
import TextInput from "@/components/global/TextInput.vue";
import TextareaInput from "@/components/global/TextareaInput.vue";
import axios from "axios";
import dayjs from "dayjs";
import { de } from "date-fns/locale";
require("dayjs/locale/de");

const props = defineProps({
    order: Object,
});
const { order } = toRefs(props);
const _order = ref(null);

const emitter = inject("emitter");

const errors = ref([]);
const customers = ref([]);
const picked_date = ref(null);
const dates_with_menu = ref(null);
const weekmenu = ref({});
const ordermeals_to_delete = ref([]);

const $toast = useToast();

onMounted(async () => {
    customers.value = (await axios.get("customers/all")).data;
    dates_with_menu.value = (await axios.get("dates_with_menu")).data.dates_with_menu;
});

watch(order, function () {
    if (order.value.date) picked_date.value = order.value.date;
    else if (!picked_date.value) picked_date.value = dayjs();
    ordermeals_to_delete.value = [];

    _order.value = order.value;

    setExistingOrder();
});

watch(picked_date, function () {
    if (!picked_date.value) picked_date.value = dayjs();

    setExistingOrder();

    loadMenuByDate();
});

const setExistingOrder = () => {
    axios.get("customer/" + order.value.customer.id + "/order_by_date/" + dayjs(picked_date.value).format("YYYY-MM-DD")).then((resp) => {
        if (resp.data.order) {
            _order.value = { ...resp.data.order, options: _order.value.options };
        } else {
            _order.value = order.value;
        }
    });
};

const addMeal = () => {
    if (!_order.value.ordermeals) _order.value.ordermeals = [];
    _order.value.ordermeals.push({ meal: { id: null }, quantity: 1 });
};
const deleteOrderMeal = (ordermeal_to_delete) => {
    if (ordermeal_to_delete.id && ordermeals_to_delete.value.findIndex((otd) => otd.id === ordermeal_to_delete.id) === -1) {
        ordermeals_to_delete.value.push(ordermeal_to_delete);
    }
    _order.value.ordermeals = _order.value.ordermeals.filter((ordermeal) => ordermeal !== ordermeal_to_delete);

    if (!_order.value.ordermeals.length) {
        addMeal();
    }
};

const saveOrder = async (close = true) => {
    errors.value = [];
    let saved_order = {};

    try {
        if (_order.value.id) {
            let data = {
                order_id: _order.value.id,
                customer_id: _order.value.customer.id,
                ordermeals: _order.value.ordermeals,
                ordermeals_to_delete: ordermeals_to_delete.value,
                notes: _order.value.notes,
                is_payed: _order.value.is_payed,
                date: dayjs(picked_date.value).format("YYYY-MM-DD"),
            };
            saved_order = (await axios.post("order/" + _order.value.id + "?_method=PUT", data)).data.order;
        } else {
            let data = {
                customer_id: _order.value.customer.id,
                ordermeals: _order.value.ordermeals,
                notes: _order.value.notes,
                is_payed: _order.value.is_payed,
                date: dayjs(picked_date.value).format("YYYY-MM-DD"),
            };
            saved_order = (await axios.post("order", data)).data.order;
        }

        $toast.success("Bestellung gespeichert!");
        emitter.emit("order-saved", saved_order);
        if (close) {
            closeModal();
        } else {
            ordermeals_to_delete.value = [];
            if (dayjs(picked_date.value).day() == 6) {
                picked_date.value = dayjs(picked_date.value).add(2, "days");
            } else {
                picked_date.value = dayjs(picked_date.value).add(1, "day");
            }
        }
    } catch (error) {
        let error_string = "";
        if (error.response.data.error) {
            error_string = error.response.data.error;
        } else if (error.response.data.errors) {
            errors.value = error.response.data.errors;
            return;
        } else {
            error_string = error;
        }
        $toast.error("Fehler: " + error_string + "<br>Bestellung nicht gespeichert!");
    }
};

const deleteOrder = async () => {
    if (!confirm("Sind Sie sicher, dass sie diese Bestellung löschen möchten? Dies kann nicht rückgängig gemacht werden!")) return;

    errors.value = [];

    try {
        await axios.delete("order/" + _order.value.id);

        $toast.success("Bestellung gelöscht!");
        emitter.emit("order-deleted", _order.value);
        closeModal();
    } catch (error) {
        let error_string = "";
        if (error.response.data.error) {
            error_string = error.response.data.error;
        } else if (error.response.data.errors) {
            errors.value = error.response.data.errors;
            return;
        } else {
            error_string = error;
        }
        $toast.error("Fehler: " + error_string + "<br>Bestellung nicht gelöscht!");
    }
};

const loadMenuByDate = async () => {
    weekmenu.value = {};

    try {
        weekmenu.value = (await axios.get("menu_by_date/" + dayjs(picked_date.value).format("YYYY-MM-DD"))).data.menu;
    } catch (error) {
        weekmenu.value.meals = [];
    }

    _order.value.ordermeals.forEach((ordermeal) => {
        if (
            !weekmenu.value.meals.filter((meal) => {
                return meal.id === ordermeal.meal.id;
            }).length
        ) {
            deleteOrderMeal(ordermeal);
        }
    });
};

const closeModal = () => {
    ordermeals_to_delete.value = [];
    emitter.emit("close-modal");
};

emitter.on("menu-saved", async () => {
    dates_with_menu.value = (await axios.get("dates_with_menu")).data.dates_with_menu;
});
</script>

<style>
.dp__input {
    border: none;
}
.dp__cell_inner {
    font-weight: bolder;
}
.dp__cell_disabled {
    font-weight: normal;
}
</style>
